import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChevronDown } from "lucide-react";

const STORAGE_KEY = "cookie-consent";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [preferences, setPreferences] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const save = (prefs: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur-sm p-4 animate-fade-in">
      <div className="max-w-2xl mx-auto space-y-3">
        <div className="flex items-start justify-between gap-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            We use cookies to improve your experience. You can choose which cookies to accept.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => save({ essential: true, analytics: false, preferences: false })}
              className="rounded-none text-[10px] tracking-widest uppercase h-7 px-3"
            >
              Essential Only
            </Button>
            <Button
              size="sm"
              onClick={() => save({ essential: true, analytics: true, preferences: true })}
              className="rounded-none text-[10px] tracking-widest uppercase h-7 px-3"
            >
              Accept All
            </Button>
          </div>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground tracking-widest uppercase transition-colors cursor-pointer"
        >
          Manage Preferences
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>

        {expanded && (
          <div className="border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-foreground">Essential</p>
                <p className="text-[10px] text-muted-foreground">Required for the site to function</p>
              </div>
              <Switch aria-label="Essential cookies always enabled" checked disabled className="opacity-50" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-foreground">Analytics</p>
                <p className="text-[10px] text-muted-foreground">Help us understand how you use the site</p>
              </div>
              <Switch aria-label="Allow analytics cookies" checked={analytics} onCheckedChange={setAnalytics} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-foreground">Preferences</p>
                <p className="text-[10px] text-muted-foreground">Remember your settings and choices</p>
              </div>
              <Switch aria-label="Allow preference cookies" checked={preferences} onCheckedChange={setPreferences} />
            </div>
            <Button
              size="sm"
              onClick={() => save({ essential: true, analytics, preferences })}
              className="rounded-none text-[10px] tracking-widest uppercase h-7 px-3"
            >
              Save Preferences
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
